import { useState, useEffect, useMemo, useRef } from 'react';
import { useI18n } from '../lib/i18n';
import { StatCard } from '../components/ui/StatCard';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import Toggle from '../components/ui/Toggle';
import { useToast } from '../components/ui/Toast';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { WorkflowModal } from '../components/webhooks/WorkflowModal';
import { WorkflowRotateModal } from '../components/webhooks/WorkflowRotateModal';
import { WorkflowCallLogDrawer } from '../components/webhooks/WorkflowCallLogDrawer';
import {
  listWebhookWorkflows,
  saveWebhookWorkflow,
  deleteWebhookWorkflow,
  type WebhookWorkflowDoc,
} from '../lib/webhookWorkflows';

export default function WebhookWorkflows() {
  const { t } = useI18n();
  const toast = useToast();
  const confirm = useConfirm();

  const [workflows, setWorkflows] = useState<WebhookWorkflowDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Modals & Drawers
  const [modalOpen, setModalOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<WebhookWorkflowDoc | null>(null);

  const [rotateModalOpen, setRotateModalOpen] = useState(false);
  const [rotatingWorkflow, setRotatingWorkflow] = useState<WebhookWorkflowDoc | null>(null);

  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const [loggingWorkflow, setLoggingWorkflow] = useState<WebhookWorkflowDoc | null>(null);

  const mountedRef = useRef(true);

  const loadData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const data = await listWebhookWorkflows();
      if (!mountedRef.current) return;
      setWorkflows(data);
    } catch (err: any) {
      if (!mountedRef.current) return;
      setError(err.message || t('webhook.page.loadError'));
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    loadData();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const stats = useMemo(() => {
    const total = workflows.length;
    const active = workflows.filter((w) => w.enabled).length;
    const totalCalls = workflows.reduce((sum, w) => sum + (w.stats?.calls || 0), 0);
    const activeCalls = workflows.filter((w) => (w.stats?.calls || 0) > 0).length;
    return { total, active, totalCalls, activeCalls };
  }, [workflows]);

  const filteredWorkflows = useMemo(() => {
    if (!searchQuery.trim()) return workflows;
    const q = searchQuery.toLowerCase();
    return workflows.filter(
      (w) =>
        w.name.toLowerCase().includes(q) ||
        w.integrationId.toLowerCase().includes(q) ||
        w.url.toLowerCase().includes(q),
    );
  }, [workflows, searchQuery]);

  const handleToggleEnabled = async (wf: WebhookWorkflowDoc) => {
    try {
      await saveWebhookWorkflow({
        id: wf.id,
        name: wf.name,
        integrationId: wf.integrationId,
        fieldMapping: wf.fieldMapping,
        actions: wf.actions,
        enabled: !wf.enabled,
      });
      await loadData(true);
    } catch (err) {
      toast.error((err as Error).message || t('webhook.page.toggleError'));
    }
  };

  const handleDelete = async (wf: WebhookWorkflowDoc) => {
    if (!(await confirm({
      title: t('webhook.page.deleteConfirm'),
      body: `"${wf.name}" — ${t('common.cannotUndo')}`,
      danger: true,
    }))) return;
    try {
      await deleteWebhookWorkflow(wf.id);
      await loadData(true);
    } catch (err) {
      toast.error((err as Error).message || t('webhook.page.deleteError'));
    }
  };

  const copyUrl = async (wf: WebhookWorkflowDoc) => {
    try {
      await navigator.clipboard.writeText(wf.url);
      setCopiedId(wf.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // noop
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            {t('webhook.page.title')}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('webhook.page.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => loadData(true)}
            disabled={refreshing || loading}
          >
            {refreshing ? t('common.loading') : t('common.refresh')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setEditingWorkflow(null);
              setModalOpen(true);
            }}
          >
            + {t('webhook.page.createWorkflow')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-rose-50 p-4 text-sm text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* 4 StatCards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label={t('webhook.stats.totalWorkflows')}
          value={stats.total}
        />
        <StatCard
          label={t('webhook.stats.activeWorkflows')}
          value={stats.active}
        />
        <StatCard
          label={t('webhook.stats.totalInboundCalls')}
          value={stats.totalCalls}
        />
        <StatCard
          label={t('webhook.stats.activeEndpoints')}
          value={stats.activeCalls}
        />
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('webhook.page.searchPlaceholder')}
          className="w-full sm:max-w-md rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs sm:text-sm outline-none focus:border-primary dark:border-slate-800 dark:bg-slate-900"
        />
      </div>

      {/* Workflows Directory */}
      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">
          {t('common.loading')}
        </div>
      ) : filteredWorkflows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 p-12 text-center dark:border-slate-800">
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t('webhook.page.noWorkflows')}
          </p>
          <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
            {t('webhook.page.noWorkflowsDesc')}
          </p>
          <Button
            variant="primary"
            size="sm"
            className="mt-4"
            onClick={() => {
              setEditingWorkflow(null);
              setModalOpen(true);
            }}
          >
            + {t('webhook.page.createWorkflow')}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredWorkflows.map((wf) => (
            <div
              key={wf.id}
              className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 transition-all hover:border-slate-300 dark:hover:border-slate-700 space-y-3"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                    {wf.name}
                  </h3>
                  <Badge variant="brand">{wf.integrationId || 'primary'}</Badge>
                  <Badge variant={wf.enabled ? 'success' : 'slate'}>
                    {wf.enabled ? t('webhook.page.statusActive') : t('webhook.page.statusDisabled')}
                  </Badge>
                </div>

                <div className="flex items-center gap-3">
                  <Toggle
                    on={wf.enabled}
                    onChange={() => handleToggleEnabled(wf)}
                    label={wf.enabled ? t('webhook.page.active') : t('webhook.page.disabled')}
                  />
                  <div className="flex items-center gap-1">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setRotatingWorkflow(wf);
                        setRotateModalOpen(true);
                      }}
                    >
                      {t('webhook.page.rotateBtn')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setLoggingWorkflow(wf);
                        setLogsDrawerOpen(true);
                      }}
                    >
                      {t('webhook.page.logsBtn')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingWorkflow(wf);
                        setModalOpen(true);
                      }}
                    >
                      {t('common.edit')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(wf)}
                      className="text-rose-600 hover:text-rose-700"
                    >
                      {t('common.delete')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Endpoint URL bar */}
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 p-2 border border-slate-200 dark:bg-slate-900/60 dark:border-slate-800">
                <span className="text-xs font-semibold text-slate-400">POST</span>
                <code className="text-xs font-mono text-slate-700 dark:text-slate-300 flex-1 truncate">
                  {wf.url}
                </code>
                <Button variant="secondary" size="sm" onClick={() => copyUrl(wf)}>
                  {copiedId === wf.id ? t('common.copied') : t('common.copy')}
                </Button>
              </div>

              {/* Mappings & Actions Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1 text-xs">
                <div>
                  <span className="text-[11px] font-semibold uppercase text-slate-400 block mb-1">
                    {t('webhook.page.fieldMappings')}:
                  </span>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {Object.entries(wf.fieldMapping || {}).map(([k, v]) => (
                      <span
                        key={k}
                        className="rounded bg-slate-100 px-2 py-0.5 text-slate-700 dark:bg-slate-800 dark:text-slate-300 font-mono text-[11px]"
                      >
                        {k}: {v}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="text-[11px] font-semibold uppercase text-slate-400 block mb-1">
                    {t('webhook.page.actionPipeline')}:
                  </span>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {(wf.actions || []).map((a, i) => (
                      <span
                        key={i}
                        className="rounded bg-violet-50 text-violet-700 border border-violet-200 px-2 py-0.5 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-900 font-medium text-[11px]"
                      >
                        {i + 1}. {t(`webhook.actionType.${a.type}`)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Stats Footer */}
              <div className="flex items-center gap-4 text-xs text-slate-400 border-t border-slate-100 dark:border-slate-800 pt-2">
                <span>
                  {t('webhook.stats.totalInboundCalls')}:{' '}
                  <strong className="text-slate-700 dark:text-slate-200">
                    {wf.stats?.calls || 0}
                  </strong>
                </span>
                <span>•</span>
                <span>
                  {t('webhook.logs.lastCalled')}:{' '}
                  {wf.stats?.lastCalledAt
                    ? new Date(wf.stats.lastCalledAt).toLocaleString()
                    : '—'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals & Drawers */}
      <WorkflowModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        workflow={editingWorkflow}
        onSaved={() => loadData(true)}
      />

      <WorkflowRotateModal
        open={rotateModalOpen}
        onClose={() => setRotateModalOpen(false)}
        workflow={rotatingWorkflow}
      />

      <WorkflowCallLogDrawer
        open={logsDrawerOpen}
        onClose={() => setLogsDrawerOpen(false)}
        workflow={loggingWorkflow}
      />
    </div>
  );
}
